import { useTranslation } from "react-i18next";
import useDialog from "../../utils/hooks/useDialog";

// Shared chrome for the Templates modals: backdrop (click outside to close),
// Escape-to-close, the top-right X, plus focus trap/restore + body-scroll lock —
// all dismissal disabled while `busy`.
export default function ModalShell({ onClose, busy = false, maxWidth = "max-w-md", children }) {
  const { t } = useTranslation();
  // Block Escape while busy by routing the hook's close through a no-op.
  const dialogRef = useDialog(busy ? () => {} : onClose);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      {/* HEIGHT IS CAPPED AND THE BODY SCROLLS — same shape as the Users
          ModalShell, which this one had drifted from.

          The panel used to grow to whatever its children needed inside a
          `flex items-center` box with no overflow anywhere. A tall dialog —
          the lesson form is ~1100px with the room block open — therefore
          overflowed the viewport in BOTH directions: flex centring pushes the
          top edge to a negative offset, and nothing scrolls, so on a laptop
          you could see neither the class/subject fields nor the Save button,
          with no way to reach them. `max-h-full` (the flex container's content
          box = viewport minus this wrapper's padding) plus a scrolling body
          makes every dialog fit any window, and short ones are unchanged
          because the cap only bites when the content exceeds it.

          `min-h-0` on the body is required: a flex child's default
          `min-height: auto` refuses to shrink below its content, which would
          keep the panel overflowing and leave the scrollbar dead. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} max-h-full flex flex-col p-5 sm:p-6 relative focus:outline-none`}
      >
        {/* Sits above the scrolling body (z-10) and carries its own background,
            so content passing underneath does not collide with the glyph. */}
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label={t("common.close")}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-lg bg-white/90 backdrop-blur-sm text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition disabled:opacity-50"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {/* `-mr-2 pr-2` parks the scrollbar in the panel's own padding instead
            of eating 8px off every field's width. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain -mr-2 pr-2">
          {children}
        </div>
      </div>
    </div>
  );
}
